import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Navigate, useParams } from "react-router-dom";
import {
  ArrowBack
} from "@mui/icons-material";
import { Button, Tooltip, Fab } from "@mui/material";
import Grid from '@mui/material/Grid2';
import {
  Campaign, 
  CampaignCharOptions, 
  CampaignNote, 
  CampaignPlayers, 
  Character, 
  CharacterOptions,
} from "@dndModels";

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";

import StringBox from "@components/input/strings/StringBox";
import LoadingBox from "@components/input/LoadingBox";

import CharacterOptionsInput from "@dndComponents/character/misc/CharacterOptionsInput";
// import CampaignOrgInstancesInput from "@dndComponents/character/misc/CampaignOrgInstancesInput";
import TabMenu from '@components/input/menus/TabMenu';
import DescriptionBox from '@dndComponents/DescriptionBox';
import AddButton from '@components/input/buttons/AddButton';
import CampaignCharactersInput from './components/CampaignCharactersInput';
import CampaignPlayersInput from './components/CampaignPlayersInput';
import CampaignNotesInput from './components/CampaignNotesInput';
import CampaignEncountersInput from './components/CampaignEncountersInput';

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

export default function CampaignGMScreen() {
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [logging_in, setLoggingIn] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const [obj, setObj] = useState<Campaign>(new Campaign());
  const [tab, setTab] = useState("Details");
  const [ping, setPing] = useState(false);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const check_login = useCallback( async () => {
    setTimeout(() => {
      if (api.user === null) {
        check_login();
      } else {
        setLoggingIn(false);
        load();
      }
    }, 1000);
  }, []);

  const loadObject = useCallback(async (campaign_id: string) => {
    // debugger;
    const campaign = await api.getCampaign({ campaign_id, include: ["players", "characters"] });
    if (campaign) {
      setObj(campaign);
    }
    setLoading(false);
  }, []);

  const load = useCallback(() => {
    console.log("load()");
    if (id && id !== "" && obj._id !== id) {
      loadObject(id);
    } else {
      setLoading(false);
    }
    setLoggingIn(false);
  }, [id, obj, loadObject]);
  
  useEffect(() => {
    if(!fetched) {
      setFetched(true);
      setLoading(true);
      if (api.user === null) {
        const userObjStr = localStorage.getItem("loginUser");
        if (!userObjStr) {
          setNavigateTo("/");
        } else {
          setLoggingIn(true);
          check_login();
        }
      } else {
        load();
      }
    }
  }, [fetched, load, check_login]);
    
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const saveCampaign = useCallback( async (campaign: Campaign) => {
    setProcessing(true);
    const last_updated = common_util.get_time_number();
    campaign.last_updated = last_updated;
    if (campaign._id && campaign._id !== "") {
      await api.updateObject("campaign", campaign);
    } else {
      campaign.owner_id = api.user ? api.user._id : "No Login";
      await api.createObject("campaign", campaign);
    }
    setProcessing(false);
  }, []);

  const campaignChanged = useCallback(() => { 
    console.log("Campaign Save Tools - Changed");
    setTimeout(() => {
      saveCampaign(obj);
    }, 100);
  }, [obj]);

  useEffect(() => {
    console.log("Campaign Save Tools");
    obj.subscription_holder.subscribe("Campaign", mounted, campaignChanged);
  }, [obj, campaignChanged]);

  const renderTab = () => {
    const user = api.user;
    if (user) {
      if (tab === "Details") {
        return (
          <>
            <Grid size={12}>
              <StringBox 
                value={obj.name} 
                message={obj.name.length > 0 ? "" : "Name Invalid"} 
                name="Name"
                onBlur={(changed: string) => {
                  const new_obj = obj;
                  new_obj.name = changed;
                  setObj(new_obj);
                  new_obj.changed();
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid size={12}>
              <DescriptionBox 
                value={obj.description} 
                name="Description"
                onChange={(changed: string) => {
                  const new_obj = obj;
                  new_obj.description = changed;
                  setObj(new_obj);
                  new_obj.changed();
                  setPing(!ping);
                }}
              />
            </Grid>
          </>
        ); 
      } else if (tab === "Characters") {
        return (
          <Grid size={12}>
            <CampaignCharactersInput
              campaign={obj}
            />
          </Grid>
        );
      } else if (tab === "Players") {
        return (
          <Grid size={12}>
            <CampaignPlayersInput
              obj={obj}
            />
          </Grid>
        ); 
      } else if (tab === "Options") {
        return (
          <Grid size={12}>
            <CharacterOptionsInput 
              obj={obj}
              disabled={processing}
              onChange={ async (options: CharacterOptions) => {
                // debugger;
                const new_obj = obj;
                let c_char_options = new_obj.char_options;
                if (!c_char_options) {
                  c_char_options = new CampaignCharOptions();
                  c_char_options.campaign_id = new_obj._id;
                  c_char_options.campaign = new_obj;
                } 
                c_char_options.char_options = options.clone();
                await api.upsertObject("campaign_char_options", c_char_options);
                new_obj.char_options = c_char_options;
                new_obj.last_updated = common_util.get_time_number();
                await api.upsertObject("campaign", new_obj);
                setObj(new_obj);
              }}
            />
          </Grid>
        ); 
      } else if (tab === "NPCs") {
        return (
          <Grid container spacing={0} direction="row">
          <Grid size={12}>NPCs Coming Soon</Grid>
          <Grid size={12}>
            This is similar to Notes below, but they can have 
            Stat Blocks or Character Sheets associated with them.
            It also has tools for keeping track of products they sell and services they provide.
            They can also use Rollable Tables for determining those things,
            and it also has tools for helping the GM generate NPCs.
          </Grid>
          </Grid>
        ); 
      } else if (tab === "Notes") {
        return (
          <CampaignNotesInput
            obj={obj}
          />
        ); 
      } else if (tab === "Encounters") {
        return (
          <Grid size={12}>
            <CampaignEncountersInput
              campaign={obj}
            />
          </Grid>
        );
      } else {
        // if (tab === "Organizations") {
        //   return (
        //     <Grid size={12}>
        //       <CampaignOrgInstancesInput 
        //         campaign={obj}
        //         onChange={(new_obj: Campaign) => {
        //           setObj(new_obj);
        //           setDoSubmit(true);
        //         }}
        //       />
        //     </Grid>
        //   ); 
        // } else if (tab === "Recipes") {
        //   return (
        //     <Grid container spacing={0} direction="row">
        //       { api.user && (obj.owner_id === api.user._id || api.user.admin) &&
        //         <>
        //           <Grid size={12}>
        //             <ToggleButtonBox
        //               name="Set Campaign Recipes"
        //               value={ api.recipes_for_campaign !== null && api.recipes_for_campaign._id === obj._id }
        //               onToggle={() => {
        //                 api.recipes_for_campaign = api.recipes_for_campaign !== null && api.recipes_for_campaign._id === obj._id ? null : obj;
        //                 setPing(!ping);
        //               }}
        //             />
        //           </Grid> 
        //           <Grid size={12}>
        //             If you select this option, you can then set recipes for items and magic item keywords to be used specifically for this campaign, on the pages for them.
        //           </Grid>
        //         </>
        //       }
        //       <Grid size={12}>
        //         Campaign Recipes
        //       </Grid>
        //     </Grid>
        //   );
        // }
      }
    } 
    return (<Grid>Coming Soon</Grid>);
  }
  
  const height = window.innerHeight - 64;
  if (navigateTo !== null) {
    return <Navigate to={navigateTo} />;
  } else if (!api.online) {
    return (
      <>Campaign Editing only works while online.  Try reconnecting.</>
    );
  } else if (!fetched || loading || logging_in) {
    return (<LoadingBox size={height * 0.5} height={height} width={window.innerWidth} />);
  } else { 
    const menu = ["Details","Characters","Players","Options","NPCs","Notes","Encounters"];
    // if (obj.character_options.use_domt) {
    //   menu.push("Deck of Many Things");
    // }
    return (
      <Grid container spacing={0} direction="row">
        <Grid size={12}
          container spacing={0} direction="row">
          <Grid size={1} className="centered">
            <Tooltip title={`Back to Campaigns`}>
              <Fab size="small" color="primary" style={{marginLeft: "8px"}}
                onClick={ () => {
                  setNavigateTo(`/campaign`);
                }}>
                <ArrowBack/>
              </Fab>
            </Tooltip> 
          </Grid>
          <Grid size={2} className="header centered">
            { obj._id === "" ? "Create Campaign" : `Edit ${obj.name}` }
          </Grid>
          <Grid size={9}>
            <TabMenu selected={tab} onSelect={setTab} options={menu} />
          </Grid>
        </Grid>
        { renderTab() }
      </Grid>
    ); 
  }
}